/**
 * @module utils/visuals/field-modes
 * @description Particle field designs — switch with `?field=` or Shift+F.
 *
 * Default `signal` is the canvas-signal bead field (circular dots, trails, bloom).
 * Also: `ribbons`, `ticks`, `filaments`.
 */

import type { Particle, ParticleRenderStyle } from './particle'
import { PARTICLE_INK_DARK, PARTICLE_INK_LIGHT } from './particle'
import { BRAND_COPPER_RGB } from './shape-design-spec'
import { curl, flowAngle, valueNoise } from './noise'
import { seededRandom } from './config'
import type { CanvasSurface } from './surface-tune'

export const FIELD_MODES = ['signal', 'ribbons', 'ticks', 'filaments'] as const
export type FieldMode = (typeof FIELD_MODES)[number]
export const DEFAULT_FIELD_MODE: FieldMode = 'signal'

export const TRAIL_CAP = 16
export const HERO_TRAIL_CAP = 26

export interface FieldRenderOpts {
  ctx: CanvasRenderingContext2D
  particles: Particle[]
  w: number
  h: number
  isDarkMode: boolean
  surface: CanvasSurface
  reducedMotion: boolean
  time: number
  audioLevel: number
  scrollProgress: number
  shape?: string
  mode?: ParticleRenderStyle['mode']
  isMorphing?: boolean
  weatherCondition?: ParticleRenderStyle['weatherCondition']
  handheld?: boolean
  lowFps?: boolean
}

type Rgb = [number, number, number]

function rgba(rgb: Rgb, a: number): string {
  return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${Math.max(0, Math.min(1, a)).toFixed(3)})`
}

function inkFor(isDarkMode: boolean): Rgb {
  return isDarkMode ? PARTICLE_INK_DARK : PARTICLE_INK_LIGHT
}

/** Copper is a signal — a handful of strands, never a modulo pattern. */
function isCopper(p: Particle): boolean {
  if (p.mass === 'dust') return false
  return seededRandom(p.index * 67 + 11) > 0.972
}

/** ~2% slower, heavier strands the eye can actually follow. */
function isHero(p: Particle): boolean {
  return p.mass === 'anchor' && seededRandom(p.index * 53 + 7) > 0.78
}

function trailCapFor(p: Particle): number {
  return isHero(p) ? HERO_TRAIL_CAP : TRAIL_CAP
}

/**
 * Ribbons smear across frames, so the canvas is faded rather than cleared.
 * Ticks and filaments redraw clean every frame.
 */
export function fieldTrailFade(
  mode: FieldMode,
  isDarkMode: boolean,
  opts?: {
    surface?: CanvasSurface
    visualMode?: ParticleRenderStyle['mode']
    researchActive?: boolean
  },
): number | null {
  if (mode === 'ticks' || mode === 'filaments') return null
  if (mode === 'ribbons') return isDarkMode ? 0.1 : 0.05
  let clear = 0.16
  if (opts?.visualMode === 'thinking' || opts?.researchActive) clear = Math.min(clear, 0.13)
  if (opts?.visualMode === 'speaking') clear = 0.28
  if (opts?.surface === 'chat') clear = Math.max(clear, opts.visualMode === 'idle' ? 0.34 : 0.26)
  return clear
}

/** Quiet flow-aligned grain — paper tooth, not a second particle language. */
function renderDustGrain(o: FieldRenderOpts): void {
  const { ctx, particles, isDarkMode, surface, reducedMotion, time } = o
  const ink = inkFor(isDarkMode)
  const gain = surfaceGain(surface, reducedMotion)
  const t = time * 0.00006
  const stride = surface === 'chat' ? 2 : 1

  for (let i = 0; i < particles.length; i += stride) {
    const p = particles[i]!
    if (p.mass !== 'dust' || p.resolvedAlpha < 0.04) continue
    if (seededRandom(p.index * 41) < 0.38) continue

    const depth = Math.max(0.35, Math.min(1.8, p.depthScale))
    const heading =
      reducedMotion || p.getSpeed() < 0.15
        ? flowAngle(p.x * 0.0075, p.y * 0.0075, t)
        : Math.atan2(p.vy, p.vx)
    const len = (2.4 + seededRandom(p.index * 19) * 3.8) * depth
    const a = p.resolvedAlpha * gain * 0.11 * (0.4 + depth * 0.28)

    ctx.save()
    ctx.translate(p.x, p.y)
    ctx.rotate(heading)
    ctx.fillStyle = rgba(ink, a)
    ctx.fillRect(-len * 0.5, -0.35, len, 0.7)
    ctx.restore()
  }
}

export function isFieldMode(value: unknown): value is FieldMode {
  return typeof value === 'string' && (FIELD_MODES as readonly string[]).includes(value)
}

function surfaceGain(surface: CanvasSurface, reducedMotion = false): number {
  let gain = surface === 'landing' ? 1 : surface === 'chat' ? 0.62 : 0.78
  // RM freezes flow — ribbons need a touch more ink so the settled shape still reads.
  if (reducedMotion) gain *= surface === 'chat' ? 1.22 : 1.28
  return gain
}

/** True when the trail has moved enough to build a ribbon outline. */
export function trailHasSpread(
  tx: Float32Array,
  ty: Float32Array,
  cap: number,
  minDistSq = 9,
): boolean {
  for (let s = 1; s < cap; s++) {
    const dx = tx[s]! - tx[0]!
    const dy = ty[s]! - ty[0]!
    if (dx * dx + dy * dy > minDistSq) return true
  }
  return false
}

/**
 * When flow is frozen, synthesize a short strand along the field tangent so
 * ribbons still read as ink strokes at settled shape positions.
 */
export function synthesizeFlowTrail(
  p: Particle,
  cap: number,
  heading: number,
  length: number,
): void {
  let tx = p.trailX
  let ty = p.trailY
  if (!tx || !ty || tx.length !== cap) {
    tx = new Float32Array(cap)
    ty = new Float32Array(cap)
    p.trailX = tx
    p.trailY = ty
  }
  const nx = Math.cos(heading)
  const ny = Math.sin(heading)
  const denom = cap > 1 ? cap - 1 : 1
  for (let s = 0; s < cap; s++) {
    const u = s / denom
    tx[s] = p.x - nx * length * u
    ty[s] = p.y - ny * length * u
  }
}

function staticRibbonLength(p: Particle, hero: boolean): number {
  const depth = Math.max(0.4, Math.min(2.1, p.depthScale))
  const base = hero ? 54 : p.mass === 'anchor' ? 38 : p.mass === 'mid' ? 26 : 16
  return base * depth
}

// ── A. Flow ribbons ────────────────────────────────────────────────────────

/**
 * Calligraphic weight: the stroke swells just behind the nib and thins to
 * nothing at the tail. A linear taper is what makes particle trails look cheap.
 */
export function ribbonWidth(u: number): number {
  return Math.sin(Math.PI * Math.min(1, 0.18 + u * 0.82)) ** 0.85 * (1 - u) ** 1.3
}

const edgeLX = new Float32Array(HERO_TRAIL_CAP)
const edgeLY = new Float32Array(HERO_TRAIL_CAP)
const edgeRX = new Float32Array(HERO_TRAIL_CAP)
const edgeRY = new Float32Array(HERO_TRAIL_CAP)

/** Longest believable gap between two consecutive trail samples, squared. */
const MAX_SEGMENT_SQ = 4096

/**
 * Offset the trail along its normals into a closed outline, so the ribbon is
 * one filled shape with a continuous edge instead of stacked line segments.
 * Returns the usable sample count, truncated at any teleport.
 */
function buildRibbonEdges(
  tx: Float32Array,
  ty: Float32Array,
  cap: number,
  halfWidth: number,
  grainSeed: number,
): number {
  let count = cap
  for (let s = 1; s < cap; s++) {
    const dx = tx[s]! - tx[s - 1]!
    const dy = ty[s]! - ty[s - 1]!
    if (dx * dx + dy * dy > MAX_SEGMENT_SQ) {
      count = s
      break
    }
  }
  if (count < 3) return 0

  for (let s = 0; s < count; s++) {
    const prev = s === 0 ? 0 : s - 1
    const next = s === count - 1 ? s : s + 1
    let dx = tx[next]! - tx[prev]!
    let dy = ty[next]! - ty[prev]!
    const len = Math.hypot(dx, dy)
    if (len < 1e-4) {
      dx = 1
      dy = 0
    } else {
      dx /= len
      dy /= len
    }
    const u = s / (count - 1)
    // Slight noise on the edge so the outline is not mathematically perfect.
    const grain = 0.86 + valueNoise(grainSeed + s * 0.6, grainSeed * 0.5) * 0.28
    const w = Math.max(0.05, halfWidth * ribbonWidth(u) * grain)
    edgeLX[s] = tx[s]! - dy * w
    edgeLY[s] = ty[s]! + dx * w
    edgeRX[s] = tx[s]! + dy * w
    edgeRY[s] = ty[s]! - dx * w
  }
  return count
}

/** Smooth an edge with quadratic segments through sample midpoints. */
function traceEdge(
  ctx: CanvasRenderingContext2D,
  xs: Float32Array,
  ys: Float32Array,
  count: number,
  reverse: boolean,
): void {
  if (reverse) {
    for (let s = count - 1; s > 0; s--) {
      const mx = (xs[s]! + xs[s - 1]!) * 0.5
      const my = (ys[s]! + ys[s - 1]!) * 0.5
      ctx.quadraticCurveTo(xs[s]!, ys[s]!, mx, my)
    }
    ctx.lineTo(xs[0]!, ys[0]!)
    return
  }
  for (let s = 0; s < count - 1; s++) {
    const mx = (xs[s]! + xs[s + 1]!) * 0.5
    const my = (ys[s]! + ys[s + 1]!) * 0.5
    ctx.quadraticCurveTo(xs[s]!, ys[s]!, mx, my)
  }
  ctx.lineTo(xs[count - 1]!, ys[count - 1]!)
}

/**
 * Curl noise advects each particle; its position history is filled as a
 * tapered ribbon. Scroll rotates the whole field so it combs on scroll.
 */
export function renderRibbons(o: FieldRenderOpts): void {
  const { ctx, particles, isDarkMode, surface, reducedMotion, time, audioLevel, scrollProgress } = o
  const ink = inkFor(isDarkMode)
  const gain = surfaceGain(surface, reducedMotion)
  const comb = scrollProgress * 0.8
  const cosC = Math.cos(comb)
  const sinC = Math.sin(comb)
  const speed = reducedMotion ? 0 : 0.9 * (1 + audioLevel * 0.5)
  const t = time * 0.00006
  const vec: [number, number] = [0, 0]

  renderDustGrain(o)

  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  for (let layer = 0; layer < 2; layer++) {
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i]!
      if (p.resolvedAlpha < 0.05) continue
      if (p.mass === 'dust') continue
      if (layer === 0 && p.mass !== 'mid') continue
      if (layer === 1 && p.mass !== 'anchor') continue
      if (p.mass === 'mid' && seededRandom(p.index * 23) < 0.36) continue

      const hero = isHero(p)
      if (speed > 0) {
        curl(p.x * 0.0075, p.y * 0.0075, t, vec)
        const fx = vec[0] * cosC - vec[1] * sinC
        const fy = vec[0] * sinC + vec[1] * cosC
        const push = hero ? speed * 0.42 : p.mass === 'anchor' ? speed * 0.72 : speed
        p.vx += fx * push
        p.vy += fy * push
      }

      const cap = trailCapFor(p)
      p.pushTrail(cap)
      const tx = p.trailX
      const ty = p.trailY
      if (!tx || !ty) continue

      const heading = flowAngle(p.x * 0.0075, p.y * 0.0075, t) + scrollProgress * 0.9
      if (reducedMotion || !trailHasSpread(tx, ty, cap)) {
        synthesizeFlowTrail(p, cap, heading, staticRibbonLength(p, hero))
      }

      const depth = Math.max(0.4, Math.min(2.1, p.depthScale))
      const base = hero ? 5.4 : p.mass === 'anchor' ? 2.35 : 0.95
      const halfWidth = base * depth * 0.5
      const count = buildRibbonEdges(tx, ty, cap, halfWidth, p.index * 0.37)
      if (count === 0) continue

      const copper = isCopper(p)
      const alpha =
        p.resolvedAlpha * gain * (0.22 + depth * 0.2) * (hero ? 1.35 : p.mass === 'anchor' ? 1.05 : 0.72)

      ctx.beginPath()
      ctx.moveTo(edgeLX[0]!, edgeLY[0]!)
      traceEdge(ctx, edgeLX, edgeLY, count, false)
      traceEdge(ctx, edgeRX, edgeRY, count, true)
      ctx.closePath()
      ctx.fillStyle = rgba(ink, alpha)
      ctx.fill()

      if (hero || copper) {
        ctx.beginPath()
        ctx.moveTo(tx[0]!, ty[0]!)
        for (let s = 0; s < count - 1; s++) {
          const mx = (tx[s]! + tx[s + 1]!) * 0.5
          const my = (ty[s]! + ty[s + 1]!) * 0.5
          ctx.quadraticCurveTo(tx[s]!, ty[s]!, mx, my)
        }
        ctx.lineWidth = Math.max(0.35, halfWidth * (hero ? 0.36 : 0.55))
        ctx.strokeStyle = rgba(copper ? BRAND_COPPER_RGB : ink, Math.min(1, alpha * (copper ? 0.9 : 1.1)))
        ctx.stroke()
      }

      if (hero) {
        const nib = halfWidth * ribbonWidth(0)
        if (nib > 0.45) {
          ctx.beginPath()
          ctx.arc(tx[0]!, ty[0]!, nib * 0.72, 0, Math.PI * 2)
          ctx.fillStyle = rgba(copper ? BRAND_COPPER_RGB : ink, Math.min(1, alpha * 1.45))
          ctx.fill()
        }
      }
    }
  }
}

// ── B. Editorial ticks ─────────────────────────────────────────────────────

/**
 * A hairline field. Every tick is exactly 1px thick and rotates toward the
 * flow tangent, so a morph reads as instruments realigning, not confetti.
 */
export function renderTicks(o: FieldRenderOpts): void {
  const { ctx, particles, isDarkMode, surface, reducedMotion, time, audioLevel, scrollProgress } = o
  const ink = inkFor(isDarkMode)
  const gain = surfaceGain(surface, reducedMotion)
  const stride = surface === 'landing' ? 1 : 2
  const t = time * 0.00006
  const pulse = reducedMotion ? 1 : 0.72 + Math.sin(time * 0.0016) * 0.28

  for (let i = 0; i < particles.length; i += stride) {
    const p = particles[i]!
    if (p.resolvedAlpha < 0.05) continue

    const target = flowAngle(p.x * 0.0075, p.y * 0.0075, t) + scrollProgress * 0.9
    if (reducedMotion) {
      p.angle = target
    } else {
      let delta = target - p.angle
      while (delta > Math.PI) delta -= Math.PI * 2
      while (delta < -Math.PI) delta += Math.PI * 2
      p.angle += delta * 0.06
    }

    const copper = isCopper(p)
    const depth = Math.max(0.4, Math.min(2.1, p.depthScale))

    if (p.mass === 'anchor') {
      // Registration mark: open square plus centre dot, drawn on the pixel grid.
      const s = isHero(p) ? 7 : 5
      const x = Math.round(p.x) + 0.5
      const y = Math.round(p.y) + 0.5
      const a = p.resolvedAlpha * gain * 0.9 * pulse
      ctx.strokeStyle = rgba(copper ? BRAND_COPPER_RGB : ink, a)
      ctx.lineWidth = 1
      ctx.strokeRect(x - s * 0.5, y - s * 0.5, s, s)
      ctx.fillStyle = rgba(copper ? BRAND_COPPER_RGB : ink, Math.min(1, a * 1.4))
      ctx.fillRect(x - 0.5, y - 0.5, 1, 1)
      continue
    }

    const len =
      ((p.mass === 'mid' ? 11 : 6) + seededRandom(p.index * 31 + 5) * 5) * depth +
      audioLevel * 6
    const a = p.resolvedAlpha * gain * (p.mass === 'mid' ? 0.62 : 0.4) * (0.55 + depth * 0.3)

    ctx.save()
    ctx.translate(p.x, p.y)
    ctx.rotate(p.angle)
    ctx.fillStyle = rgba(copper ? BRAND_COPPER_RGB : ink, a)
    ctx.fillRect(-len * 0.5, -0.5, len, 1)
    // Leading end-cap: reads as direction without breaking the 1px rule.
    if (p.mass === 'mid') {
      ctx.fillRect(len * 0.5 - 1, -1, 1, 2)
    }
    ctx.restore()
  }
}

// ── C. Light filaments ─────────────────────────────────────────────────────

/**
 * Sparse long curves. Two orbiting control points make an S-curve that breathes
 * in place rather than travelling — light rather than matter.
 */
export function renderFilaments(o: FieldRenderOpts): void {
  const { ctx, particles, isDarkMode, surface, reducedMotion, time, audioLevel, scrollProgress } = o
  const ink = inkFor(isDarkMode)
  const gain = surfaceGain(surface, reducedMotion)
  const target = surface === 'landing' ? 90 : surface === 'chat' ? 44 : 64
  const stride = Math.max(1, Math.floor(particles.length / target))
  const drift = reducedMotion ? 0 : time * 0.0004

  ctx.lineCap = 'round'

  for (let i = 0; i < particles.length; i += stride) {
    const p = particles[i]!
    if (p.resolvedAlpha < 0.05) continue

    const seed = seededRandom(p.index * 71 + 13) * Math.PI * 2
    const span = 60 + seededRandom(p.index * 17 + 3) * 100
    const heading = seed + scrollProgress * 0.6
    const nx = Math.cos(heading)
    const ny = Math.sin(heading)
    const ax = p.x - nx * span * 0.5
    const ay = p.y - ny * span * 0.5
    const bx = p.x + nx * span * 0.5
    const by = p.y + ny * span * 0.5
    const swing = 90 * (0.4 + audioLevel * 0.6)
    const s1 = Math.cos(drift + seed) * swing
    const s2 = Math.sin(drift * 1.3 + seed) * swing * -0.8
    const c1x = ax + nx * span * 0.3 - ny * s1
    const c1y = ay + ny * span * 0.3 + nx * s1
    const c2x = bx - nx * span * 0.3 - ny * s2
    const c2y = by - ny * span * 0.3 + nx * s2

    const copper = isCopper(p)
    const rgb = copper ? BRAND_COPPER_RGB : ink
    const a = p.resolvedAlpha * gain * (copper ? 0.16 : 0.09)

    // Two stacked passes: a soft body, then a brighter spine. Not a glow stack.
    for (let layer = 0; layer < 2; layer++) {
      ctx.beginPath()
      ctx.moveTo(ax, ay)
      ctx.bezierCurveTo(c1x, c1y, c2x, c2y, bx, by)
      ctx.lineWidth = layer === 0 ? 1.35 : 0.55
      ctx.strokeStyle = rgba(rgb, a * (layer === 0 ? 0.45 : 0.95))
      ctx.stroke()
    }

    if (copper) {
      ctx.beginPath()
      ctx.arc(p.x, p.y, 2.1, 0, Math.PI * 2)
      ctx.lineWidth = 1
      ctx.strokeStyle = rgba(BRAND_COPPER_RGB, Math.min(1, a * 4))
      ctx.stroke()
    }
  }
}

/**
 * Canvas-signal look: depth-sorted circular beads, speed trails, sparse bloom.
 * Dark surfaces composite with `lighter` so overlaps read as mist, not mud.
 */
export function renderSignal(o: FieldRenderOpts): void {
  const { ctx, particles, isDarkMode, surface, reducedMotion, time, audioLevel, shape, mode } = o
  const lite = o.handheld === true || o.lowFps === true
  const order = particles.map((_, i) => i)
  if (!lite) order.sort((a, b) => particles[a]!.depthScale - particles[b]!.depthScale)

  const bloom = !reducedMotion && !lite
  const bloomIntensity =
    surface === 'chat'
      ? mode === 'speaking' ? 0.76 : mode === 'thinking' ? 0.68 : 0.48
      : mode === 'speaking' ? 1.1 : mode === 'thinking' ? 0.82 : 0.64

  if (isDarkMode) ctx.globalCompositeOperation = 'lighter'

  for (let i = 0; i < order.length; i++) {
    particles[order[i]!]!.drawSimple(ctx, isDarkMode, shape, {
      time,
      mode: mode ?? 'idle',
      audioLevel,
      bloom,
      bloomIntensity,
      isMorphing: o.isMorphing ?? false,
      surface,
      reducedMotion,
      weatherCondition: o.weatherCondition,
    })
  }

  ctx.globalCompositeOperation = 'source-over'
}

export function renderField(mode: FieldMode, o: FieldRenderOpts): void {
  if (mode === 'ticks') return renderTicks(o)
  if (mode === 'filaments') return renderFilaments(o)
  if (mode === 'ribbons') return renderRibbons(o)
  return renderSignal(o)
}
